import { useContext } from 'react';
import { EntityTypeEnum, NotificationCenterMessageResponseOutDTO, SupplierNotificationTypeEnum } from '@api/ringil-api.v2';
import { ApiContext } from '@api/api';
import useNavigation from '@hooks/useNavigation';
import { isNullOrUndefined } from '@utils/utils';
import { isMessageNotification } from './notificationCenterLabels';

/**
 * Opening a notification is the same gesture in the list and in the menu badge: mark it read (unless it
 * already is) and go to the entity it was raised for. Shared so both stay in sync.
 */
const useNotificationCenterNavigation = () => {
  const nav = useNavigation();
  const { readNotificationMessages } = useContext(ApiContext);

  const navigateToEntity = (notification: NotificationCenterMessageResponseOutDTO) => {
    if (isNullOrUndefined(notification.entityId)) return;
    switch (notification.entityType) {
      case EntityTypeEnum.SUPPLIER_NOTIFICATION:
        nav.navigate(nav.urlFunctions.createSupplierNotificationDetail(notification.entityId, SupplierNotificationTypeEnum.EFXX));
        break;
      case EntityTypeEnum.GROUP_DEMAND:
        nav.navigate(nav.urlFunctions.createMultiDemanDetail(notification.entityId));
        break;
      case EntityTypeEnum.TIMESLOT:
        nav.navigate(nav.urlFunctions.createReservationDetail(notification.entityId));
        break;
      default:
        nav.navigate(nav.urlFunctions.createExpeditionDetailForUser(notification.entityId, notification.expeditionCoordinate, isMessageNotification(notification.type)));
    }
  };

  /** `onRead` runs only when the notification actually changed state — e.g. to refresh a badge count. */
  const markAsReadAndNavigate = (notification: NotificationCenterMessageResponseOutDTO, onRead?: () => void) => {
    if (notification.isRead) {
      navigateToEntity(notification);
      return;
    }
    readNotificationMessages({ ids: [notification.id] }, () => {
      onRead?.();
      navigateToEntity(notification);
    });
  };

  return { markAsReadAndNavigate };
};

export default useNotificationCenterNavigation;
